import { Icon } from '../icons';
import React, { useEffect, useRef, useState } from 'react';

import {
  AnyClip, AspectRatio, AudioClip, ClipFraming, ClipSelection,
  ProjectAsset, TextClip, Timeline, Transition, TransitionType, VideoClip,
} from '../../types/editor';
import ClipFrameThumbnail from './ClipFrameThumbnail';
import TextStylePreview from './TextStylePreview';
import { saveTextDefaults } from './editorState';
import {
  TEXT_FONT_FAMILIES, TEXT_FONT_LABELS, framingFor, isDefaultFraming,
} from './migrateTimeline';

const MAX_IMAGE_DUR = 30;
const MAX_TRANSITION = 5;
const MIN_DURATION = 0.05;

/* ---- time helpers ----------------------------------------------------
 * The Start / End / Duration fields are MM:SS:CS strings (centiseconds
 * in the third group). Easy to read at a glance, precise enough for
 * frame-adjacent editing, and short enough to type by hand. parseTime
 * returns null on anything it can't make sense of so we can revert. */
function pad2(n: number): string { return String(n).padStart(2, '0'); }

function formatTime(sec: number): string {
  let v = sec;
  if (!isFinite(v) || v < 0) v = 0;
  const totalCs = Math.round(v * 100);
  const m = Math.floor(totalCs / 6000);
  const s = Math.floor((totalCs % 6000) / 100);
  const cs = totalCs % 100;
  return `${pad2(m)}:${pad2(s)}:${pad2(cs)}`;
}

function parseTime(s: string): number | null {
  const parts = s.trim().split(':');
  if (parts.length !== 3) return null;
  const m = Number(parts[0]);
  const sec = Number(parts[1]);
  const cs = Number(parts[2]);
  if (!Number.isFinite(m) || !Number.isFinite(sec) || !Number.isFinite(cs)) return null;
  if (sec < 0 || sec >= 60 || cs < 0 || cs >= 100 || m < 0) return null;
  return m * 60 + sec + cs / 100;
}

function findClip(timeline: Timeline, selection: ClipSelection | null): AnyClip | TextClip | null {
  if (!selection) return null;
  const list = timeline[selection.track]?.clips || [];
  return (list as unknown as AnyClip[]).find((c) => c.id === selection.id) || null;
}

/* TimeField: text input that stays controlled while editing but only
 * commits a parsed value on blur or Enter. Invalid input reverts to
 * the prop value so the field can never desync from clip state. */
interface TimeFieldProps {
  label: string;
  value: number;
  onCommit: (next: number) => void;
}

function TimeField({ label, value, onCommit }: TimeFieldProps) {
  const [text, setText] = useState<string>(() => formatTime(value));
  const lastValueRef = useRef(value);

  /* External changes (timeline-drag, etc) update the display value
   * unless the user is actively typing — track the "last committed
   * value" via a ref so we can distinguish. */
  useEffect(() => {
    if (Math.abs(lastValueRef.current - value) < 0.005) return;
    lastValueRef.current = value;
    setText(formatTime(value));
  }, [value]);

  function commit(): void {
    const parsed = parseTime(text);
    if (parsed === null) {
      setText(formatTime(value));
      return;
    }
    lastValueRef.current = parsed;
    onCommit(parsed);
    setText(formatTime(parsed));
  }

  return (
    <label className="inspector-time-cell">
      <span className="muted inspector-time-label">{label}</span>
      <input
        type="text"
        className="inspector-time-input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { (e.target as HTMLInputElement).blur(); }
        }}
        inputMode="numeric"
        placeholder="MM:SS:CS"
      />
    </label>
  );
}

interface ClipInspectorProps {
  selection: ClipSelection | null;
  timeline: Timeline;
  assets: ProjectAsset[];
  currentAspect: AspectRatio;
  onTrim: (patch: Partial<AnyClip>) => void;
  onUpdate: (patch: Partial<AnyClip>) => void;
  onDelete: () => void;
  onClose: () => void;
  onUpdateFraming: (clipId: string, patch: Partial<ClipFraming>) => void;
  onResetFraming: (clipId: string) => void;
  /** Broadcast the given clip patch to every other video-track clip
   *  whose source asset shares the active clip's kind ('image' vs
   *  'video'). Used by the "Apply to all similar" affordances on the
   *  Transition / Dynamics rows. */
  onApplyPatchToSimilar: (patch: Partial<AnyClip>, kind: 'image' | 'video') => void;
  /** Broadcast a full framing record to every other video-track clip
   *  of the same kind, under the active aspect. */
  onApplyFramingToSimilar: (framing: ClipFraming, kind: 'image' | 'video') => void;
  /** Set every image clip's duration to `duration` and re-pack the
   *  video track back-to-back (keeps order, removes gaps/overlaps). */
  onApplyDurationToImages: (duration: number) => void;
}

export default function ClipInspector({
  selection, timeline, assets, currentAspect,
  onTrim, onUpdate, onDelete, onClose,
  onUpdateFraming, onResetFraming,
  onApplyPatchToSimilar, onApplyFramingToSimilar, onApplyDurationToImages,
}: ClipInspectorProps) {
  /* Confirmation modal state for Remove Clip. Local — the parent
   * doesn't need to know it exists; once confirmed we call onDelete
   * which is what closes the inspector too. */
  const [confirmRemove, setConfirmRemove] = useState(false);

  if (!selection) return null;
  const clip = findClip(timeline, selection);
  if (!clip) return null;
  const isTextTrack = selection.track === 'overlay';
  const asset = isTextTrack ? undefined
    : assets.find((a) => a.id === (clip as AnyClip).assetId);
  const isVideoTrack = selection.track === 'video';

  /* "Similar" = other video-track clips whose source asset shares
   * this clip's kind (image-vs-video). Used to count and to gate the
   * "Apply to all similar" buttons — there's nothing to broadcast if
   * the user hasn't added any other clips of the same kind yet. */
  const clipKind: 'image' | 'video' | null = asset?.kind === 'image'
    ? 'image'
    : asset?.kind === 'video'
      ? 'video'
      : null;
  const similarCount = clipKind ? timeline.video.clips.filter((c) => {
    if (c.id === clip.id) return false;
    const a = assets.find((x) => x.id === c.assetId);
    return a?.kind === clipKind;
  }).length : 0;
  const similarLabel = clipKind === 'image' ? 'images' : 'videos';
  const sourceDur = asset?.duration || null;
  const maxTrimOut = isTextTrack
    ? 600
    : (sourceDur && asset?.kind !== 'image' ? sourceDur : MAX_IMAGE_DUR);

  /* Derived timeline numbers. Source-side trim (trimIn / trimOut) is
   * abstracted away: the user thinks in "Start / End / Duration" on
   * the project timeline, and we map back to source trims so the
   * underlying clip rules still hold. Snapshot the trim-in value
   * outside the closures so TypeScript keeps the null-narrowing on
   * `clip` and the commit handlers don't need to re-derive it. */
  const trimIn = clip.trimIn;
  const duration = Math.max(MIN_DURATION, clip.trimOut - clip.trimIn);
  const start = Math.max(0, clip.start);
  const end = start + duration;

  function commitStart(nextStart: number): void {
    const s = Math.max(0, nextStart);
    onTrim({ start: s });
  }

  function commitDuration(nextDur: number): void {
    const dur = Math.max(MIN_DURATION, nextDur);
    /* Keep trimIn fixed; bump trimOut so the source window extends.
     * Capped at the source's natural length for video/audio so the
     * user can't ask for more frames than exist. Image clips are free
     * to extend up to MAX_IMAGE_DUR. */
    const newTrimOut = Math.min(maxTrimOut, trimIn + dur);
    onTrim({ trimOut: newTrimOut });
  }

  function commitEnd(nextEnd: number): void {
    const e = Math.max(start + MIN_DURATION, nextEnd);
    commitDuration(e - start);
  }

  const videoClip = isVideoTrack ? (clip as VideoClip) : null;
  const textClip = isTextTrack ? (clip as unknown as TextClip) : null;

  /* Text style edits also persist as the default for the NEXT text
   * clip (font, colors, radius, size, nudge — not the content). */
  function updateTextStyle(patch: Partial<TextClip>): void {
    onUpdate(patch as Partial<AnyClip>);
    const { text: _text, ...style } = patch;
    if (Object.keys(style).length) saveTextDefaults(style);
  }
  const audioClip = !isVideoTrack && !isTextTrack ? (clip as AudioClip) : null;

  const framing = (videoClip || textClip)
    ? framingFor(timeline, currentAspect, clip.id)
    : null;
  const framingCustomized = framing ? !isDefaultFraming(framing) : false;

  return (
    <div
      className="modal modal--centered"
      role="dialog"
      aria-modal="true"
      aria-label="Edit clip"
      /* No backdrop-click dismiss: mid-edit misclicks outside the panel
         shouldn't close the inspector. Done / × / Esc close it. */
    >
      <div className="modal__panel">
        <header className="modal__head">
          <h3>
            {isTextTrack ? 'Text overlay' : (asset?.original_filename || 'Clip')}
            {' '}<span className="muted">· {isTextTrack ? 'text' : selection.track}</span>
          </h3>
          <button
            type="button"
            className="modal__close"
            onClick={onClose}
            aria-label="Close"
          ><Icon name="close" /></button>
        </header>

        <div className="modal__body">
          {/* Audio clips keep the simple stacked layout; video clips
              use a 2/3 + 1/3 grid — options left, framing preview in a
              right-hand column. */}
          {!videoClip && (
            <div className="inspector-time-row">
              <TimeField label="Start" value={start} onCommit={commitStart} />
              <TimeField label="End"   value={end}   onCommit={commitEnd}   />
              <TimeField label="Duration" value={duration} onCommit={commitDuration} />
            </div>
          )}

          {videoClip && (
            <div className="inspector-columns">
            <div className="inspector-col-main">
              {/* Timeline row: editing any field updates the trim so
                  end = start + duration always holds. */}
              <div className="inspector-time-row">
                <TimeField label="Start" value={start} onCommit={commitStart} />
                <TimeField label="End"   value={end}   onCommit={commitEnd}   />
                <TimeField label="Duration" value={duration} onCommit={commitDuration} />
              </div>
              {asset?.kind === 'image' && similarCount > 0 && (
                <button
                  type="button"
                  className="ghost btn--sm inspector-apply-duration"
                  onClick={() => onApplyDurationToImages(duration)}
                  title="Give every photo clip this duration and re-pack the sequence with no gaps"
                >
                  Apply duration to all {similarCount + 1} photos
                </button>
              )}
              <div className="inspector-field">
                <div className="inspector-section-head">
                  <span className="section-label">Transition in</span>
                  {similarCount > 0 && clipKind && (
                    <button
                      type="button"
                      className="ghost btn--sm inspector-apply-all"
                      onClick={() => onApplyPatchToSimilar(
                        { transitionIn: videoClip.transitionIn },
                        clipKind,
                      )}
                      title={`Copy this transition to every other ${similarLabel === 'images' ? 'image' : 'video'} clip on the timeline`}
                    >
                      Apply to {similarCount} other {similarCount === 1 ? similarLabel.slice(0, -1) : similarLabel}
                    </button>
                  )}
                </div>
                <div className="inspector-segmented">
                  {(['cut', 'crossfade'] as TransitionType[]).map((t) => (
                    <button
                      key={t}
                      type="button"
                      className={(videoClip.transitionIn?.type || 'cut') === t ? 'active' : ''}
                      onClick={() => {
                        const trans: Transition = {
                          type: t,
                          duration: videoClip.transitionIn?.duration ?? 0.5,
                        };
                        onUpdate({ transitionIn: trans });
                      }}
                    >{t}</button>
                  ))}
                </div>
              </div>
              {(videoClip.transitionIn?.type === 'crossfade') && (
                <label className="inspector-field inspector-slider-field">
                  <span className="inspector-slider-head">
                    <span>Transition length</span>
                    <span className="muted">({(videoClip.transitionIn?.duration ?? 0.5).toFixed(2)}s)</span>
                  </span>
                  <input
                    type="range" min={0.1} max={MAX_TRANSITION} step={0.1}
                    value={videoClip.transitionIn?.duration ?? 0.5}
                    onChange={(e) => {
                      const trans: Transition = { type: 'crossfade', duration: parseFloat(e.target.value) };
                      onUpdate({ transitionIn: trans });
                    }}
                  />
                </label>
              )}

              {/* Dynamics — aspect-independent motion overlays applied
                  on top of static framing. Subtle by design: small
                  motion that adds life without distracting. */}
              <div className="inspector-section">
                <div className="inspector-section-head">
                  <span className="section-label">Dynamics</span>
                  {similarCount > 0 && clipKind && (
                    <button
                      type="button"
                      className="ghost btn--sm inspector-apply-all"
                      onClick={() => onApplyPatchToSimilar(
                        { dynamics: videoClip.dynamics ? { ...videoClip.dynamics } : {} },
                        clipKind,
                      )}
                      title={`Copy these pan/zoom toggles to every other ${similarLabel === 'images' ? 'image' : 'video'} clip on the timeline`}
                    >
                      Apply to {similarCount} other {similarCount === 1 ? similarLabel.slice(0, -1) : similarLabel}
                    </button>
                  )}
                </div>
                <label className="inspector-toggle">
                  <input
                    type="checkbox"
                    checked={!!videoClip.dynamics?.pan}
                    onChange={(e) => onUpdate({
                      dynamics: { ...videoClip.dynamics, pan: e.target.checked },
                    })}
                  />
                  <span>Subtle pan</span>
                  <span className="muted">slow left → right drift</span>
                </label>
                <label className="inspector-toggle">
                  <input
                    type="checkbox"
                    checked={!!videoClip.dynamics?.zoom}
                    onChange={(e) => onUpdate({
                      dynamics: { ...videoClip.dynamics, zoom: e.target.checked },
                    })}
                  />
                  <span>Subtle zoom</span>
                  <span className="muted">slow zoom-in over clip</span>
                </label>
              </div>

              {asset?.kind === 'video' && (
                <label className="inspector-field inspector-slider-field">
                  <span className="inspector-slider-head">
                    <span>Original audio volume</span>
                    <span className="muted">({((videoClip.volume ?? 1) * 100).toFixed(0)}%)</span>
                  </span>
                  <input
                    type="range" min={0} max={2} step={0.05}
                    value={videoClip.volume ?? 1}
                    onChange={(e) => onUpdate({ volume: parseFloat(e.target.value) })}
                  />
                  <span className="muted inspector-slider-hint">
                    The clip's own soundtrack in the final mix — 0% mutes it;
                    it blends with anything on the audio track.
                  </span>
                </label>
              )}
            </div>

            <div className="inspector-col-side">
              {framing && (
                <div className="inspector-framing">
                  <div className="inspector-framing-head">
                    <span className="section-label">Framing — {currentAspect}</span>
                    <span className="inspector-framing-head-actions">
                      {similarCount > 0 && clipKind && (
                        <button
                          type="button"
                          className="ghost btn--sm inspector-apply-all"
                          onClick={() => onApplyFramingToSimilar({ ...framing }, clipKind)}
                          title={`Copy zoom + pan (${currentAspect}) to every other ${similarLabel === 'images' ? 'image' : 'video'} clip on the timeline`}
                        >
                          Apply to {similarCount} other {similarCount === 1 ? similarLabel.slice(0, -1) : similarLabel}
                        </button>
                      )}
                      {framingCustomized && (
                        <button
                          type="button"
                          className="ghost btn--sm"
                          onClick={() => onResetFraming(videoClip.id)}
                          title="Reset zoom and pan to the default fit-inside framing for this aspect"
                        >Reset</button>
                      )}
                    </span>
                  </div>

                  {/* Live preview of the current clip frame in the
                      currently-active aspect, with the user's framing
                      applied. Updates as the sliders move — so the
                      user sees what they're doing without flipping
                      back to the main editor preview. */}
                  <div className="inspector-thumb-row">
                    <ClipFrameThumbnail
                      clip={videoClip}
                      asset={asset}
                      aspect={currentAspect}
                      framing={framing}
                    />
                  </div>
                  {/* Zoom on its own row; pan H + pan V paired on a second
                      row. Values are inlined next to the label so the
                      stack takes less vertical space. */}
                  <label className="inspector-field inspector-slider-field">
                    <span className="inspector-slider-head">
                      <span>Zoom</span>
                      <span className="muted">({framing.scale.toFixed(2)}×)</span>
                    </span>
                    <input
                      type="range" min={0.5} max={8} step={0.05}
                      value={framing.scale}
                      onChange={(e) => onUpdateFraming(videoClip.id,
                                                        { scale: parseFloat(e.target.value) })}
                    />
                  </label>
                  <div className="inspector-pan-row">
                    <label className="inspector-field inspector-slider-field">
                      <span className="inspector-slider-head">
                        <span>Pan horizontal</span>
                        <span className="muted">({(framing.offsetX * 100).toFixed(0)}%)</span>
                      </span>
                      <input
                        type="range" min={-1} max={1} step={0.02}
                        value={framing.offsetX}
                        onChange={(e) => onUpdateFraming(videoClip.id,
                                                          { offsetX: parseFloat(e.target.value) })}
                      />
                    </label>
                    <label className="inspector-field inspector-slider-field">
                      <span className="inspector-slider-head">
                        <span>Pan vertical</span>
                        <span className="muted">({(framing.offsetY * 100).toFixed(0)}%)</span>
                      </span>
                      <input
                        type="range" min={-1} max={1} step={0.02}
                        value={framing.offsetY}
                        onChange={(e) => onUpdateFraming(videoClip.id,
                                                          { offsetY: parseFloat(e.target.value) })}
                      />
                    </label>
                  </div>
                </div>
              )}
            </div>
            </div>
          )}

          {textClip && framing && (
            <>
            <TextStylePreview clip={textClip} />
            <div className="inspector-columns">
            <div className="inspector-col-main">
              <div className="inspector-time-row">
                <TimeField label="Start" value={start} onCommit={commitStart} />
                <TimeField label="End"   value={end}   onCommit={commitEnd}   />
                <TimeField label="Duration" value={duration} onCommit={commitDuration} />
              </div>
              <label className="inspector-field">
                <span className="section-label">Text</span>
                <textarea
                  className="inspector-textarea"
                  rows={3}
                  maxLength={300}
                  value={textClip.text}
                  onChange={(e) => onUpdate({ text: e.target.value } as Partial<AnyClip>)}
                  placeholder="Caption text…"
                />
              </label>
              <div className="inspector-pan-row">
                <label className="inspector-field">
                  <span className="section-label">Font</span>
                  <select
                    value={textClip.font || 'sans'}
                    style={{ fontFamily: TEXT_FONT_FAMILIES[textClip.font || 'sans'] }}
                    onChange={(e) => updateTextStyle({ font: e.target.value as TextClip['font'] })}
                  >
                    {(Object.keys(TEXT_FONT_LABELS) as Array<keyof typeof TEXT_FONT_LABELS>).map((k) => (
                      <option key={k} value={k} style={{ fontFamily: TEXT_FONT_FAMILIES[k] }}>
                        {TEXT_FONT_LABELS[k]}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="inspector-field inspector-slider-field">
                  <span className="inspector-slider-head">
                    <span>Size</span>
                    <span className="muted">({Math.round(textClip.fontSize * 100)}% of height)</span>
                  </span>
                  <input
                    type="range" min={0.02} max={0.2} step={0.005}
                    value={textClip.fontSize}
                    onChange={(e) => updateTextStyle({ fontSize: parseFloat(e.target.value) })}
                  />
                </label>
              </div>
              <div className="inspector-color-row">
                <label className="inspector-field">
                  <span className="section-label">Text color</span>
                  <input
                    type="color"
                    className="inspector-color"
                    value={textClip.color || '#ffffff'}
                    onChange={(e) => updateTextStyle({ color: e.target.value })}
                  />
                </label>
                <label className="inspector-field">
                  <span className="section-label">Outline</span>
                  <input
                    type="color"
                    className="inspector-color"
                    value={textClip.outlineColor || '#000000'}
                    onChange={(e) => updateTextStyle({ outlineColor: e.target.value })}
                  />
                </label>
                <label className="inspector-field">
                  <span className="section-label">Background</span>
                  <span className="inspector-bg-controls">
                    <input
                      type="checkbox"
                      checked={!!textClip.bgColor}
                      onChange={(e) => updateTextStyle(
                        { bgColor: e.target.checked ? '#000000' : '' },
                      )}
                      aria-label="Enable background box"
                    />
                    {textClip.bgColor ? (
                      <input
                        type="color"
                        className="inspector-color"
                        value={textClip.bgColor}
                        onChange={(e) => updateTextStyle({ bgColor: e.target.value })}
                      />
                    ) : (
                      <span className="muted">None</span>
                    )}
                  </span>
                </label>
              </div>
            </div>

            <div className="inspector-col-side">
              <div className="inspector-framing">
                <div className="inspector-framing-head">
                  <span className="section-label">Position — {currentAspect}</span>
                  {framingCustomized && (
                    <button
                      type="button"
                      className="ghost btn--sm"
                      onClick={() => onResetFraming(textClip.id)}
                      title="Reset zoom and position for this aspect"
                    >Reset</button>
                  )}
                </div>
                <span className="muted inspector-slider-hint">
                  Saved per aspect ratio — watch the main preview while adjusting.
                </span>
                <label className="inspector-field inspector-slider-field">
                  <span className="inspector-slider-head">
                    <span>Zoom</span>
                    <span className="muted">({framing.scale.toFixed(2)}×)</span>
                  </span>
                  <input
                    type="range" min={0.25} max={4} step={0.05}
                    value={framing.scale}
                    onChange={(e) => onUpdateFraming(textClip.id,
                                                      { scale: parseFloat(e.target.value) })}
                  />
                </label>
                <div className="inspector-pan-row">
                  <label className="inspector-field inspector-slider-field">
                    <span className="inspector-slider-head">
                      <span>H Pos.</span>
                      <span className="muted">({(framing.offsetX * 100).toFixed(0)}%)</span>
                    </span>
                    <input
                      type="range" min={-1} max={1} step={0.02}
                      value={framing.offsetX}
                      onChange={(e) => onUpdateFraming(textClip.id,
                                                        { offsetX: parseFloat(e.target.value) })}
                    />
                  </label>
                  <label className="inspector-field inspector-slider-field">
                    <span className="inspector-slider-head">
                      <span>V Pos.</span>
                      <span className="muted">({(framing.offsetY * 100).toFixed(0)}%)</span>
                    </span>
                    <input
                      type="range" min={-1} max={1} step={0.02}
                      value={framing.offsetY}
                      onChange={(e) => onUpdateFraming(textClip.id,
                                                        { offsetY: parseFloat(e.target.value) })}
                    />
                  </label>
                </div>
                <div className="inspector-pan-row">
                  {textClip.bgColor ? (
                    <label className="inspector-field inspector-slider-field">
                      <span className="inspector-slider-head">
                        <span>Corner radius</span>
                        <span className="muted">({Math.round((textClip.bgRadius ?? 0) * 100)}%)</span>
                      </span>
                      <input
                        type="range" min={0} max={1} step={0.05}
                        value={textClip.bgRadius ?? 0}
                        onChange={(e) => updateTextStyle({ bgRadius: parseFloat(e.target.value) })}
                      />
                    </label>
                  ) : <span />}
                  <label className="inspector-field inspector-slider-field">
                    <span className="inspector-slider-head">
                      <span>Text V offset</span>
                      <span className="muted">({Math.round((textClip.textOffsetY ?? 0) * 100)}%)</span>
                    </span>
                    <input
                      type="range" min={-0.5} max={0.5} step={0.02}
                      value={textClip.textOffsetY ?? 0}
                      onChange={(e) => updateTextStyle({ textOffsetY: parseFloat(e.target.value) })}
                    />
                  </label>
                </div>
              </div>
            </div>
            </div>
            </>
          )}

          {audioClip && (
            <label className="inspector-field">
              <span className="section-label">Volume</span>
              <input
                type="range" min={0} max={2} step={0.05}
                value={audioClip.volume ?? 1}
                onChange={(e) => onUpdate({ volume: parseFloat(e.target.value) })}
              />
              <span className="muted">{((audioClip.volume ?? 1) * 100).toFixed(0)}%</span>
            </label>
          )}
        </div>

        <footer className="modal__foot modal__foot--split">
          {/* Remove Clip pinned left, Done pinned right via the split
              modifier. Remove uses the danger--strong variant so it
              reads red in its resting state, not just on hover. */}
          <button
            type="button"
            className="danger danger--strong"
            onClick={() => setConfirmRemove(true)}
          >
            Remove Clip
          </button>
          <button type="button" className="primary" onClick={onClose}>Done</button>
        </footer>
      </div>

      {/* Remove confirmation closes only through its action buttons. */}
      {confirmRemove && (
        <div
          className="modal modal--centered modal--confirm"
          role="dialog"
          aria-modal="true"
          aria-label="Confirm remove"
        >
          <div className="modal__panel modal__panel--narrow">
            <header className="modal__head">
              <h3>Remove clip?</h3>
            </header>
            <div className="modal__body">
              <p>Are you sure you want to remove this clip from the timeline?</p>
            </div>
            <footer className="modal__foot">
              <button
                type="button"
                className="ghost"
                onClick={() => setConfirmRemove(false)}
              >
                No
              </button>
              <button
                type="button"
                className="danger danger--strong"
                onClick={() => { setConfirmRemove(false); onDelete(); }}
              >
                Yes, remove
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
